import type { StateCreator } from "zustand";
import type { DBOpsState } from "../useStudioAPI/hooks/dbOps.store";
import type {
  EmailTemplate,
  EmailAttachment,
  BusinessUnit,
  Region,
  ExtraJSON,
  ProcessKind,
  ProcessStatus,
  ObjectStatus,
  ProcessPDFTemplate,
  AlertCategory,
  CommentFlagOption,
  UserGroup,
  ObjectTypeWorkflowKindRelation,
  IntegrationToken,
  IntegrationConfig
} from "@certa/queries/types/appSettings.types";
import { mutate } from "@certa/studio-core/src/state/stateMutators";
import type {
  BaseAppSettings,
  BaseAppSettingsRaw,
  MegaJSON,
  ReferencedAppSettings,
  ReferencedAppSettingsRaw
} from "@certa/studio-core";
import {
  appSettingsModelCreator,
  appSettingsPayloadCreator,
  alertCategoryPayloadCreator,
  businessUnitPayloadCreator,
  commentFlagOptionPayloadCreator,
  emailAttachmentPayloadCreator,
  emailTemplatePayloadCreator,
  extraJSONPayloadCreator,
  groupPayloadCreator,
  objectTypeWorkflowKindRelationPayloadCreator,
  processKindPayloadCreator,
  processPDFTemplatePayloadCreator,
  processStatusPayloadCreator,
  objectStatusPayloadCreator,
  regionPayloadCreator,
  integrationConfigPayloadCreator,
  integrationTokenPayloadCreator
} from "@certa/queries/models/appSettings.model";

export const getReferencedAppSettingsModelCreator = (
  appSettingsRaw: ReferencedAppSettingsRaw
): ReferencedAppSettings => {
  const appSettings = appSettingsModelCreator({
    ...appSettingsRaw,
    integrationconfig: appSettingsRaw.integrationconfig || [],
    integrationtoken: appSettingsRaw.integrationtoken || [],
    file_paths: []
  });

  // Use destructuring to exclude properties not in ReferencedAppSettings
  const { feExtraMetaData, ...referencedAppSettings } = appSettings;

  return referencedAppSettings;
};

export const getAppSettingsFromMegaJSON = (
  megaJSON: MegaJSON
): ReferencedAppSettings => {
  const appSettingsRaw = megaJSON.app_settings;
  if (!appSettingsRaw) return emptyReferencedAppSettings;

  return structuredClone(getReferencedAppSettingsModelCreator(appSettingsRaw));
};

export const getBaseAppSettingsFromMegaJSON = (
  megaJSON: MegaJSON
): BaseAppSettings => {
  const baseAppSettingsRaw = megaJSON.base_app_settings;
  if (!baseAppSettingsRaw) return emptyBaseAppSettings;

  const { feExtraMetaData, ...baseAppSettings } = structuredClone(
    appSettingsModelCreator({
      ...baseAppSettingsRaw,
      file_paths: [],
      frontend_extra_metadata: {}
    })
  );

  return structuredClone(baseAppSettings);
};

export const getReferencedAppSettingsPayloadCreator = (
  appSettings: ReferencedAppSettings
): ReferencedAppSettingsRaw => {
  const appSettingsRaw = appSettingsPayloadCreator(appSettings);
  return appSettingsRaw;
};

const getBaseAppSettingsPayloadCreator = (
  baseAppSettings: BaseAppSettings
): BaseAppSettingsRaw => {
  const { frontend_extra_metadata: feExtraMetaData, ...baseAppSettingsRaw } =
    appSettingsPayloadCreator({
      ...baseAppSettings,
      feExtraMetaData: {}
    });
  return baseAppSettingsRaw;
};

export const emptyReferencedAppSettings: ReferencedAppSettings = {
  emailTemplate: [],
  emailAttachment: [],
  businessUnit: [],
  region: [],
  extraJSON: [],
  processKind: [],
  processStatus: [],
  objectStatus: [],
  processPDFTemplate: [],
  alertCategory: [],
  commentFlagOption: [],
  group: [],
  objectTypeWorkflowKindRelation: [],
  integrationConfig: [],
  integrationToken: []
};

const emptyBaseAppSettings: BaseAppSettings = {
  ...emptyReferencedAppSettings
};

const emptyReferencedAppSettingsRaw: ReferencedAppSettingsRaw =
  getReferencedAppSettingsPayloadCreator(emptyReferencedAppSettings);

const emptyBaseAppSettingsRaw: BaseAppSettingsRaw = {
  ...emptyReferencedAppSettingsRaw
};

export type AppSettingsSlice = {
  // Getters
  getAppSettings: () => ReferencedAppSettings;
  getAppSettingsRaw: () => ReferencedAppSettingsRaw;
  getEmailTemplate: (slug: string) => EmailTemplate | undefined;
  getEmailTemplates: () => EmailTemplate[];
  getEmailAttachment: (title: string) => EmailAttachment | undefined;
  getEmailAttachments: () => EmailAttachment[];
  getBusinessUnit: (code: string) => BusinessUnit | undefined;
  getBusinessUnits: () => BusinessUnit[];
  getRegion: (code: string) => Region | undefined;
  getRegions: () => Region[];
  getExtraJSON: (tag: string) => ExtraJSON | undefined;
  getExtraJSONs: () => ExtraJSON[];
  getProcessKind: (tag: string) => ProcessKind | undefined;
  getProcessKinds: () => ProcessKind[];
  getProcessStatus: (tag: string) => ProcessStatus | undefined;
  getProcessStatuses: () => ProcessStatus[];
  getObjectStatus: (tag: string) => ObjectStatus | undefined;
  getObjectStatuses: () => ObjectStatus[];
  getProcessPDFTemplate: (tag: string) => ProcessPDFTemplate | undefined;
  getProcessPDFTemplates: () => ProcessPDFTemplate[];
  getAlertCategory: (tag: string) => AlertCategory | undefined;
  getAlertCategories: () => AlertCategory[];
  getCommentFlagOption: (tag: string) => CommentFlagOption | undefined;
  getCommentFlagOptions: () => CommentFlagOption[];
  getUserGroup: (name: string) => UserGroup | undefined;
  getUserGroups: () => UserGroup[];
  getObjectTypeWorkflowKindRelation: (
    objectType: string,
    workflowKind: string
  ) => ObjectTypeWorkflowKindRelation | undefined;
  getObjectTypeWorkflowKindRelations: (
    relations: { objectType: string; workflowKind: string }[]
  ) => ObjectTypeWorkflowKindRelation[];
  getIntegrationConfig: (apiType: string) => IntegrationConfig | undefined;
  getIntegrationConfigs: () => IntegrationConfig[];
  getIntegrationToken: (tokenType: string) => IntegrationToken | undefined;
  getIntegrationTokens: () => IntegrationToken[];

  // Add operations
  addEmailTemplate: (emailTemplate: EmailTemplate) => void;
  addEmailAttachment: (emailAttachment: EmailAttachment) => void;
  addBusinessUnit: (businessUnit: BusinessUnit) => void;
  addRegion: (region: Region) => void;
  addExtraJSON: (extraJSON: ExtraJSON) => void;
  addProcessKind: (processKind: ProcessKind) => void;
  addProcessStatus: (processStatus: ProcessStatus) => void;
  addObjectStatus: (objectStatus: ObjectStatus) => void;
  addProcessPDFTemplate: (processPDFTemplate: ProcessPDFTemplate) => void;
  addAlertCategory: (alertCategory: AlertCategory) => void;
  addCommentFlagOption: (commentFlagOption: CommentFlagOption) => void;
  addUserGroup: (userGroup: UserGroup) => void;
  addObjectTypeWorkflowKindRelation: (
    relation: ObjectTypeWorkflowKindRelation
  ) => void;
  addIntegrationConfig: (integrationConfig: IntegrationConfig) => void;
  addIntegrationToken: (integrationToken: IntegrationToken) => void;

  // Individual setters
  setEmailTemplate: (
    emailTemplate: EmailTemplate,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setEmailAttachment: (
    emailAttachment: EmailAttachment,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setBusinessUnit: (
    businessUnit: BusinessUnit,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setRegion: (region: Region, shouldSkipUpdatingModifiedAt?: boolean) => void;
  setExtraJSON: (
    extraJSON: ExtraJSON,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setProcessKind: (
    processKind: ProcessKind,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setProcessStatus: (
    processStatus: ProcessStatus,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setObjectStatus: (
    objectStatus: ObjectStatus,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setProcessPDFTemplate: (
    processPDFTemplate: ProcessPDFTemplate,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setAlertCategory: (
    alertCategory: AlertCategory,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setCommentFlagOption: (
    commentFlagOption: CommentFlagOption,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setUserGroup: (
    userGroup: UserGroup,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setIntegrationConfig: (
    integrationConfig: IntegrationConfig,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  setIntegrationToken: (
    integrationToken: IntegrationToken,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // Bulk setters
  bulkSetEmailTemplates: (
    emailTemplates: EmailTemplate[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetEmailAttachments: (
    emailAttachments: EmailAttachment[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetBusinessUnits: (
    businessUnits: BusinessUnit[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetRegions: (
    regions: Region[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetExtraJSONs: (
    extraJSONs: ExtraJSON[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetProcessKinds: (
    processKinds: ProcessKind[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetProcessStatuses: (
    processStatuses: ProcessStatus[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetObjectStatuses: (
    objectStatuses: ObjectStatus[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetProcessPDFTemplates: (
    processPDFTemplates: ProcessPDFTemplate[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetAlertCategories: (
    alertCategories: AlertCategory[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetCommentFlagOptions: (
    commentFlagOptions: CommentFlagOption[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetUserGroups: (
    userGroups: UserGroup[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetObjectTypeWorkflowKindRelations: (
    relations: ObjectTypeWorkflowKindRelation[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetIntegrationConfigs: (
    integrationConfigs: IntegrationConfig[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;
  bulkSetIntegrationTokens: (
    integrationTokens: IntegrationToken[],
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // Total replace
  replaceAllAppSettings: (
    appSettings: ReferencedAppSettings,
    shouldSkipUpdatingModifiedAt?: boolean
  ) => void;

  // Delete operations
  deleteEmailTemplate: (slug: string) => void;
  deleteEmailAttachment: (title: string) => void;
  deleteBusinessUnit: (code: string) => void;
  deleteRegion: (code: string) => void;
  deleteExtraJSON: (tag: string) => void;
  deleteProcessKind: (tag: string) => void;
  deleteProcessStatus: (tag: string) => void;
  deleteObjectStatus: (tag: string) => void;
  deleteProcessPDFTemplate: (tag: string) => void;
  deleteAlertCategory: (tag: string) => void;
  deleteCommentFlagOption: (tag: string) => void;
  deleteUserGroup: (name: string) => void;
  deleteObjectTypeWorkflowKindRelation: (
    objectTypeWFKindRelation: ObjectTypeWorkflowKindRelation
  ) => void;
  deleteIntegrationConfig: (apiType: string) => void;
  deleteIntegrationToken: (tokenType: string) => void;

  // Base AppSettings;
  getBaseAppSettings: () => BaseAppSettings;
  getBaseAppSettingsRaw: () => BaseAppSettingsRaw;
  setBaseAppSettings: (appSettings: BaseAppSettings) => void;
};

export const appsettingsSlice: StateCreator<
  DBOpsState,
  [],
  [],
  AppSettingsSlice
> = (set, get) => ({
  // Base App Settings
  getBaseAppSettings: () => {
    const { megaJSON } = get();
    return getBaseAppSettingsFromMegaJSON(megaJSON);
  },

  getBaseAppSettingsRaw: () => {
    const { megaJSON } = get();
    return megaJSON.base_app_settings || emptyBaseAppSettingsRaw;
  },

  setBaseAppSettings: appSettings => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.updateBaseAppSettings({
        state: megaJSON,
        baseAppSettings: getBaseAppSettingsPayloadCreator(appSettings)
      })
    ];
    applyChanges(changes);
  },

  // Getters
  getAppSettings: () => {
    const { megaJSON } = get();
    return getAppSettingsFromMegaJSON(megaJSON);
  },

  getAppSettingsRaw: () => {
    const { megaJSON } = get();
    return megaJSON.app_settings || emptyReferencedAppSettingsRaw;
  },

  getEmailTemplate: (slug: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.emailTemplate.find(template => template.slug === slug)
    );
  },

  getEmailTemplates: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.emailTemplate);
  },

  getEmailAttachment: (title: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.emailAttachment.find(attachment => attachment.title === title)
    );
  },

  getEmailAttachments: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.emailAttachment);
  },

  getBusinessUnit: (code: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.businessUnit.find(unit => unit.code === code)
    );
  },

  getBusinessUnits: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.businessUnit);
  },

  getRegion: (code: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.region.find(region => region.code === code)
    );
  },

  getRegions: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.region);
  },

  getExtraJSON: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.extraJSON.find(extra => extra.tag === tag)
    );
  },

  getExtraJSONs: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.extraJSON);
  },

  getProcessKind: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.processKind.find(kind => kind.tag === tag)
    );
  },

  getProcessKinds: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.processKind);
  },

  getProcessStatus: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.processStatus.find(status => status.tag === tag)
    );
  },

  getProcessStatuses: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.processStatus);
  },

  getObjectStatus: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.objectStatus.find(status => status.tag === tag)
    );
  },

  getObjectStatuses: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.objectStatus);
  },

  getProcessPDFTemplate: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.processPDFTemplate.find(template => template.tag === tag)
    );
  },

  getProcessPDFTemplates: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.processPDFTemplate);
  },

  getAlertCategory: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.alertCategory.find(category => category.tag === tag)
    );
  },

  getAlertCategories: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.alertCategory);
  },

  getCommentFlagOption: (tag: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.commentFlagOption.find(option => option.tag === tag)
    );
  },

  getCommentFlagOptions: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.commentFlagOption);
  },

  getUserGroup: (name: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.group.find(group => group.name === name)
    );
  },

  getUserGroups: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.group);
  },

  getObjectTypeWorkflowKindRelation: (
    objectType: string,
    workflowKind: string
  ) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.objectTypeWorkflowKindRelation.find(
        relation =>
          relation.objectType === objectType &&
          relation.workflowKind === workflowKind
      )
    );
  },

  getObjectTypeWorkflowKindRelations: (
    relations: { objectType: string; workflowKind: string }[]
  ) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.objectTypeWorkflowKindRelation);
  },

  getIntegrationConfig: (apiType: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.integrationConfig.find(config => config.apiType === apiType)
    );
  },
  getIntegrationConfigs: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.integrationConfig);
  },
  getIntegrationToken: (tokenType: string) => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(
      appSettings.integrationToken.find(token => token.tokenType === tokenType)
    );
  },
  getIntegrationTokens: () => {
    const { megaJSON } = get();
    const appSettings = getAppSettingsFromMegaJSON(megaJSON);
    return structuredClone(appSettings.integrationToken);
  },

  // Add operations
  addEmailTemplate: (emailTemplate: EmailTemplate) => {
    const { megaJSON, applyChanges } = get();
    const emailTemplateRaw = emailTemplatePayloadCreator(emailTemplate);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailtemplate",
        item: emailTemplateRaw
      })
    ];
    applyChanges(changes);
  },

  addEmailAttachment: (emailAttachment: EmailAttachment) => {
    const { megaJSON, applyChanges } = get();
    const emailAttachmentRaw = emailAttachmentPayloadCreator(emailAttachment);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailattachment",
        item: emailAttachmentRaw
      })
    ];
    applyChanges(changes);
  },
  addBusinessUnit: (businessUnit: BusinessUnit) => {
    const { megaJSON, applyChanges } = get();
    const businessUnitRaw = businessUnitPayloadCreator(businessUnit);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "businessunit",
        item: businessUnitRaw
      })
    ];
    applyChanges(changes);
  },

  addRegion: (region: Region) => {
    const { megaJSON, applyChanges } = get();
    const regionRaw = regionPayloadCreator(region);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "region",
        item: regionRaw
      })
    ];
    applyChanges(changes);
  },

  addExtraJSON: (extraJSON: ExtraJSON) => {
    const { megaJSON, applyChanges } = get();
    const extraJSONRaw = extraJSONPayloadCreator(extraJSON);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "extrajson",
        item: extraJSONRaw
      })
    ];
    applyChanges(changes);
  },

  addProcessKind: (processKind: ProcessKind) => {
    const { megaJSON, applyChanges } = get();
    const processKindRaw = processKindPayloadCreator(processKind);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowkind",
        item: processKindRaw
      })
    ];
    applyChanges(changes);
  },

  addProcessStatus: (processStatus: ProcessStatus) => {
    const { megaJSON, applyChanges } = get();
    const processStatusRaw = processStatusPayloadCreator(processStatus);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowstatus",
        item: processStatusRaw
      })
    ];
    applyChanges(changes);
  },

  addObjectStatus: (objectStatus: ObjectStatus) => {
    const { megaJSON, applyChanges } = get();
    const objectStatusRaw = objectStatusPayloadCreator(objectStatus);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objectstatus",
        item: objectStatusRaw
      })
    ];
    applyChanges(changes);
  },

  addProcessPDFTemplate: (processPDFTemplate: ProcessPDFTemplate) => {
    const { megaJSON, applyChanges } = get();
    const processPDFTemplateRaw =
      processPDFTemplatePayloadCreator(processPDFTemplate);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowpdftemplate",
        item: processPDFTemplateRaw
      })
    ];
    applyChanges(changes);
  },
  addAlertCategory: (alertCategory: AlertCategory) => {
    const { megaJSON, applyChanges } = get();
    const alertCategoryRaw = alertCategoryPayloadCreator(alertCategory);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "alertcategory",
        item: alertCategoryRaw
      })
    ];
    applyChanges(changes);
  },

  addCommentFlagOption: (commentFlagOption: CommentFlagOption) => {
    const { megaJSON, applyChanges } = get();
    const commentFlagOptionRaw =
      commentFlagOptionPayloadCreator(commentFlagOption);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "commentflagoption",
        item: commentFlagOptionRaw
      })
    ];
    applyChanges(changes);
  },
  addUserGroup: (userGroup: UserGroup) => {
    const { megaJSON, applyChanges } = get();
    const userGroupRaw = groupPayloadCreator(userGroup);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "group",
        item: userGroupRaw
      })
    ];
    applyChanges(changes);
  },

  addObjectTypeWorkflowKindRelation: (
    relation: ObjectTypeWorkflowKindRelation
  ) => {
    const { megaJSON, applyChanges } = get();
    const relationRaw = objectTypeWorkflowKindRelationPayloadCreator(relation);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objecttypeworkflowkindrelation",
        item: relationRaw
      })
    ];
    applyChanges(changes);
  },
  addIntegrationConfig: (integrationConfig: IntegrationConfig) => {
    const { megaJSON, applyChanges } = get();
    const integrationConfigRaw =
      integrationConfigPayloadCreator(integrationConfig);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationconfig",
        item: integrationConfigRaw
      })
    ];
    applyChanges(changes);
  },
  addIntegrationToken: (integrationToken: IntegrationToken) => {
    const { megaJSON, applyChanges } = get();
    const integrationTokenRaw =
      integrationTokenPayloadCreator(integrationToken);
    const changes = [
      mutate.addAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationtoken",
        item: integrationTokenRaw
      })
    ];
    applyChanges(changes);
  },

  // Individual setters
  setEmailTemplate: (emailTemplate: EmailTemplate) => {
    const { megaJSON, applyChanges } = get();
    const emailTemplateRaw = emailTemplatePayloadCreator(emailTemplate);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailtemplate",
        item: emailTemplateRaw,
        identifier: "slug"
      })
    ];
    applyChanges(changes);
  },

  setEmailAttachment: (emailAttachment: EmailAttachment) => {
    const { megaJSON, applyChanges } = get();
    const emailAttachmentRaw = emailAttachmentPayloadCreator(emailAttachment);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailattachment",
        item: emailAttachmentRaw,
        identifier: "title"
      })
    ];
    applyChanges(changes);
  },
  setBusinessUnit: (businessUnit: BusinessUnit) => {
    const { megaJSON, applyChanges } = get();
    const businessUnitRaw = businessUnitPayloadCreator(businessUnit);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "businessunit",
        item: businessUnitRaw,
        identifier: "code"
      })
    ];
    applyChanges(changes);
  },

  setRegion: (region: Region) => {
    const { megaJSON, applyChanges } = get();
    const regionRaw = regionPayloadCreator(region);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "region",
        item: regionRaw,
        identifier: "code"
      })
    ];
    applyChanges(changes);
  },

  setExtraJSON: (extraJSON: ExtraJSON) => {
    const { megaJSON, applyChanges } = get();
    const extraJSONRaw = extraJSONPayloadCreator(extraJSON);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "extrajson",
        item: extraJSONRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setProcessKind: (processKind: ProcessKind) => {
    const { megaJSON, applyChanges } = get();
    const processKindRaw = processKindPayloadCreator(processKind);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowkind",
        item: processKindRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setProcessStatus: (processStatus: ProcessStatus) => {
    const { megaJSON, applyChanges } = get();
    const processStatusRaw = processStatusPayloadCreator(processStatus);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowstatus",
        item: processStatusRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setObjectStatus: (objectStatus: ObjectStatus) => {
    const { megaJSON, applyChanges } = get();
    const objectStatusRaw = objectStatusPayloadCreator(objectStatus);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objectstatus",
        item: objectStatusRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setProcessPDFTemplate: (processPDFTemplate: ProcessPDFTemplate) => {
    const { megaJSON, applyChanges } = get();
    const processPDFTemplateRaw =
      processPDFTemplatePayloadCreator(processPDFTemplate);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowpdftemplate",
        item: processPDFTemplateRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setAlertCategory: (alertCategory: AlertCategory) => {
    const { megaJSON, applyChanges } = get();
    const alertCategoryRaw = alertCategoryPayloadCreator(alertCategory);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "alertcategory",
        item: alertCategoryRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setCommentFlagOption: (commentFlagOption: CommentFlagOption) => {
    const { megaJSON, applyChanges } = get();
    const commentFlagOptionRaw =
      commentFlagOptionPayloadCreator(commentFlagOption);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "commentflagoption",
        item: commentFlagOptionRaw,
        identifier: "tag"
      })
    ];
    applyChanges(changes);
  },

  setUserGroup: (userGroup: UserGroup) => {
    const { megaJSON, applyChanges } = get();
    const userGroupRaw = groupPayloadCreator(userGroup);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "group",
        item: userGroupRaw,
        identifier: "name"
      })
    ];
    applyChanges(changes);
  },

  setIntegrationConfig: (integrationConfig: IntegrationConfig) => {
    const { megaJSON, applyChanges } = get();
    const integrationConfigRaw =
      integrationConfigPayloadCreator(integrationConfig);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationconfig",
        item: integrationConfigRaw,
        identifier: "api_type"
      })
    ];
    applyChanges(changes);
  },
  setIntegrationToken: (integrationToken: IntegrationToken) => {
    const { megaJSON, applyChanges } = get();
    const integrationTokenRaw =
      integrationTokenPayloadCreator(integrationToken);
    const changes = [
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationtoken",
        item: integrationTokenRaw,
        identifier: "token_type"
      })
    ];
    applyChanges(changes);
  },

  // Bulk setters
  bulkSetEmailTemplates: (emailTemplates: EmailTemplate[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = emailTemplates.map(emailTemplate =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailtemplate",
        item: emailTemplatePayloadCreator(emailTemplate),
        identifier: "slug"
      })
    );
    applyChanges(changes);
  },

  bulkSetEmailAttachments: (emailAttachments: EmailAttachment[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = emailAttachments.map(emailAttachment =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailattachment",
        item: emailAttachmentPayloadCreator(emailAttachment),
        identifier: "title"
      })
    );
    applyChanges(changes);
  },

  bulkSetBusinessUnits: (businessUnits: BusinessUnit[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = businessUnits.map(businessUnit =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "businessunit",
        item: businessUnitPayloadCreator(businessUnit),
        identifier: "code"
      })
    );
    applyChanges(changes);
  },

  bulkSetRegions: (regions: Region[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = regions.map(region =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "region",
        item: regionPayloadCreator(region),
        identifier: "code"
      })
    );
    applyChanges(changes);
  },

  bulkSetExtraJSONs: (extraJSONs: ExtraJSON[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = extraJSONs.map(extraJSON =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "extrajson",
        item: extraJSONPayloadCreator(extraJSON),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetProcessKinds: (processKinds: ProcessKind[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = processKinds.map(processKind =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowkind",
        item: processKindPayloadCreator(processKind),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetProcessStatuses: (processStatuses: ProcessStatus[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = processStatuses.map(processStatus =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowstatus",
        item: processStatusPayloadCreator(processStatus),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetObjectStatuses: (objectStatuses: ObjectStatus[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = objectStatuses.map(objectStatus =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objectstatus",
        item: objectStatusPayloadCreator(objectStatus),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetProcessPDFTemplates: (processPDFTemplates: ProcessPDFTemplate[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = processPDFTemplates.map(processPDFTemplate =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowpdftemplate",
        item: processPDFTemplatePayloadCreator(processPDFTemplate),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetAlertCategories: (alertCategories: AlertCategory[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = alertCategories.map(alertCategory =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "alertcategory",
        item: alertCategoryPayloadCreator(alertCategory),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetCommentFlagOptions: (commentFlagOptions: CommentFlagOption[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = commentFlagOptions.map(commentFlagOption =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "commentflagoption",
        item: commentFlagOptionPayloadCreator(commentFlagOption),
        identifier: "tag"
      })
    );
    applyChanges(changes);
  },

  bulkSetUserGroups: (userGroups: UserGroup[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = userGroups.map(userGroup =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "group",
        item: groupPayloadCreator(userGroup),
        identifier: "name"
      })
    );
    applyChanges(changes);
  },

  bulkSetObjectTypeWorkflowKindRelations: (
    relations: ObjectTypeWorkflowKindRelation[]
  ) => {
    const { megaJSON, applyChanges } = get();
    const changes = relations.map(relation =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objecttypeworkflowkindrelation",
        item: objectTypeWorkflowKindRelationPayloadCreator(relation),
        identifier: "object_type" // Note: using the raw field name from the payload
      })
    );
    applyChanges(changes);
  },

  bulkSetIntegrationConfigs: (integrationConfigs: IntegrationConfig[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = integrationConfigs.map(integrationConfig =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationconfig",
        item: integrationConfigPayloadCreator(integrationConfig),
        identifier: "api_type"
      })
    );
    applyChanges(changes);
  },
  bulkSetIntegrationTokens: (integrationTokens: IntegrationToken[]) => {
    const { megaJSON, applyChanges } = get();
    const changes = integrationTokens.map(integrationToken =>
      mutate.updateAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationtoken",
        item: integrationTokenPayloadCreator(integrationToken),
        identifier: "token_type"
      })
    );
    applyChanges(changes);
  },

  // Total replace
  replaceAllAppSettings: (appSettings: ReferencedAppSettings) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.updateAppSettings({
        state: megaJSON,
        appSettings: getReferencedAppSettingsPayloadCreator(appSettings)
      })
    ];
    applyChanges(changes);
  },

  // Delete operations
  deleteEmailTemplate: (slug: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailtemplate",
        identifier: "slug",
        identifierValue: slug
      })
    ];
    applyChanges(changes);
  },

  deleteEmailAttachment: (title: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "emailattachment",
        identifier: "title",
        identifierValue: title
      })
    ];
    applyChanges(changes);
  },

  deleteBusinessUnit: (code: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "businessunit",
        identifier: "code",
        identifierValue: code
      })
    ];
    applyChanges(changes);
  },

  deleteRegion: (code: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "region",
        identifier: "code",
        identifierValue: code
      })
    ];
    applyChanges(changes);
  },

  deleteExtraJSON: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "extrajson",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteProcessKind: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowkind",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteProcessStatus: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowstatus",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteObjectStatus: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "objectstatus",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteProcessPDFTemplate: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "workflowpdftemplate",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteAlertCategory: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "alertcategory",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteCommentFlagOption: (tag: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "commentflagoption",
        identifier: "tag",
        identifierValue: tag
      })
    ];
    applyChanges(changes);
  },

  deleteUserGroup: (name: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "group",
        identifier: "name",
        identifierValue: name
      })
    ];
    applyChanges(changes);
  },

  deleteObjectTypeWorkflowKindRelation: (
    objectTypeWFKindRelation: ObjectTypeWorkflowKindRelation
  ) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      {
        path: `app_settings.objecttypeworkflowkindrelation`,
        to: megaJSON.app_settings.objecttypeworkflowkindrelation.filter(
          relation =>
            !(
              relation.object_type === objectTypeWFKindRelation.objectType &&
              relation.workflow_kind ===
                objectTypeWFKindRelation.workflowKind &&
              relation.relation === objectTypeWFKindRelation.relation
            )
        ),
        from: megaJSON.app_settings.objecttypeworkflowkindrelation
      }
    ];
    applyChanges(changes);
  },

  deleteIntegrationConfig: (apiType: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationconfig",
        identifier: "api_type",
        identifierValue: apiType
      })
    ];
    applyChanges(changes);
  },

  deleteIntegrationToken: (tokenType: string) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutate.removeAppSettingsItem({
        state: megaJSON,
        appSettingsType: "integrationtoken",
        identifier: "token_type",
        identifierValue: tokenType
      })
    ];
    applyChanges(changes);
  }
});
